/* eslint-disable react/jsx-indent */ // TODO: Remove once linting setup is fixed

import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import styled, { css } from 'styled-components/macro'
import { useDispatch } from 'react-redux'
import { openOverlay } from 'redux/actions/ui'
import { useLongPress } from 'use-long-press'

import { DateFormats, formatRoundedPrice, isPrice } from 'utilities/format'
import { media } from 'utilities/styled'
import {
  formatMileage,
  getMonthNameByIndex,
  getObjectFromQueryString,
} from 'utilities/utils'

import {
  CarFileStatusEnumLowerCase,
  CarFileStatusEnumUpperCase,
  CarFileViewTypeEnum,
  CarIdentityPlateSizes,
} from 'config/enums'

import Definition from 'components/atoms/definition'
import Icon from 'components/atoms/icon'
import NewTooltip from 'components/atoms/new-tooltip'
import Text from 'components/atoms/text'
import CarBrandAndModel, {
  CarBrandAndModelLayoutTypes,
} from 'components/molecules/car-brand-and-model'
import CarIdentityPlate from 'components/molecules/car-identity-plate'
import CarFileCardListPricing from 'components/molecules/carfile-card-list-pricing'
import CarFileCardTitle from 'components/molecules/carfile-card-title'
import CarKeySpecs from 'components/molecules/car-key-specs'
import StockDaysBadge from 'components/molecules/stock-days-badge'
import CarFileCardBids, {
  CarFileCardBidsLayoutType,
  CarFileCardBidsSize,
  defaultProps as carFileCardBidsDefaultProps,
  propTypes as carFileCardBidsPropTypes,
} from 'components/molecules/carfile-card-bids'
import CarFileCardContextMenu from 'components/molecules/carfile-card-context-menu-container'
import MarktplaatsCarCard from './marktplaats-car-card'
import { ThemeContext } from 'styled-components'
import { useMediaLayout } from 'use-media'
import MarktplaatsViewOverlay from 'components/redux-containers/marktplaats-view-overlay-container'
import Checkbox from 'components/atoms/checkbox'
import SelectableGalleryImage from 'components/atoms/selectable-gallery-image'
import AdvertisementCarCard from './advertisement-car-card'

const smallLayout = css`
  grid-template-columns: min-content minmax(0, 3fr) min-content;
  grid-template-rows: auto min-content;
  grid-gap: 0;
  grid-template-areas: 'gallery description actions advertisement';
`

const largeLayout = css`
  grid-gap: ${({ theme }) => theme.sizings.lvl1};
  grid-template-columns: min-content minmax(0, 1fr) min-content;
  grid-template-rows: auto min-content;
  grid-template-areas:
    'gallery description licensePlate summary advertisement'
    'gallery description actions summary advertisement';
`

const standardLayout = css`
  align-items: center;
  grid-gap: ${({ theme }) => theme.sizings.lvl1};
  grid-template-columns: min-content minmax(0, 1fr) min-content;
  grid-template-rows: auto;
  grid-template-areas: 'gallery description actions';
  padding-right: ${({ theme }) => theme.sizings.lvl1};

  ${media.phone`
    grid-gap: ${({ theme }) => theme.sizeByFactor(1.5)};
    grid-template-columns: min-content minmax(0, 1fr) min-content min-content;
    grid-template-areas: 'gallery description identity summary';
    padding-right: ${({ theme }) => theme.sizeByFactor(1.5)};
  `}
`

const StyledCarCardWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
`

const Container = styled.article`
  width: 100%;
  height: 100%;
  box-shadow: ${({ theme }) => theme.shadows.bottomSoft};
  background-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.isolatedBackground : theme.colors.brandGolf};
  border: ${({ theme, $isSelected }) =>
    $isSelected && `1px solid ${theme.colors.darkestBackground}`};
  border-radius: ${({ theme }) =>
    theme.cardBorderRadius ? `${theme.cardBorderRadius}px` : '0'};
  transition: background-color 120ms ease-in-out;
  display: grid;
  ${({ $layoutSizeType }) =>
    $layoutSizeType === LayoutSizeTypes.LARGE && largeLayout};
  ${({ $layoutSizeType }) =>
    $layoutSizeType === LayoutSizeTypes.SMALL && smallLayout};
  ${({ $standard }) => $standard && standardLayout};
  position: relative;

  /* 
   * Prevent default link behaviour.
   * This allows us to use long press to select a card instead of opening the default mobile pop-up for links (e.g. 'open in new tab').
  */
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
`

const StyledGalleryImageLink = styled(Link)`
  /**
    Using 22.2 times the base size, instead of 22, for the width otherwise the rendering engine of the
    vertical height on the image is slightly off.
    Probably because of a rounding issue in the calculation of the height of the image.
   */
  width: ${({ theme, $layoutSizeType }) =>
    theme.sizeByFactor($layoutSizeType === LayoutSizeTypes.LARGE ? 22.2 : 10)};
  grid-area: gallery;
  text-decoration: none;

  ${({ theme, $standard, $layoutSizeType }) =>
    $standard &&
    `
      position: relative;
      ${
        $layoutSizeType === LayoutSizeTypes.LARGE
          ? `width: ${theme.sizeByFactor(15.75)};`
          : ''
      }
    `}

  ${({ theme, $layoutSizeType }) =>
    theme.cardBorderRadius &&
    `
      display: block;
      border-top-left-radius: ${theme.cardBorderRadius}px;
      ${
        $layoutSizeType === LayoutSizeTypes.SMALL
          ? ''
          : `border-bottom-left-radius: ${theme.cardBorderRadius}px;`
      }
      overflow: hidden;
    `}

  ${media.tv`
    height: 100%;

    & label,
    & figure {
      height: 100%;
    }
  `}
`

const DescriptionContainer = styled(Link)`
  grid-area: description;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  padding: ${({ theme }) => theme.sizeByFactor(1.5)}
    ${({ theme }) => theme.sizings.lvl2};

  ${({ $standard, theme }) =>
    $standard &&
    `
      min-width: 0;
      justify-content: center;
      gap: ${theme.sizeByFactor(0.75)};
      padding: 0;
    `}

  ${media.phone`
    ${({ $standard, theme }) =>
      $standard &&
      `
        gap: ${theme.sizings.lvl1};
        padding: ${theme.sizeByFactor(1.5)} 0;
      `}
  `}
`

const PhonePrices = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizeByFactor(0.75)};
  min-width: 0;
  font-family: ${({ theme }) => theme.font};
  font-size: ${({ theme }) => theme.smallFontSize};
  font-weight: ${({ theme }) => theme.baseFontWeight};
  line-height: ${({ theme }) => theme.smallFontSize};
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;

  > span {
    flex-shrink: 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;

    &:last-child {
      flex-shrink: 1;
    }
  }
`

const PhonePriceDivider = styled.span`
  flex-shrink: 0;
  width: 1px;
  height: ${({ theme }) => theme.sizeByFactor(1.5)};
  background-color: ${({ theme }) => theme.colors.brandDelta};
`

const IdentityColumn = styled.div`
  grid-area: identity;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  align-self: stretch;
  gap: ${({ theme }) => theme.sizeByFactor(0.75)};
  padding: ${({ theme }) => theme.sizeByFactor(1.5)} 0;
`

// Same width for license plates and VIN numbers, so the plates line up across cards
const StandardPlate = styled(CarIdentityPlate)`
  flex-shrink: 0;
  box-sizing: border-box;
  min-width: ${({ theme }) => theme.sizeByFactor(10.5)};
`

const StandardSummaryLink = styled(Link)`
  grid-area: summary;
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizings.lvl1};
  padding-top: ${({ theme }) => theme.sizeByFactor(1.5)};
  text-decoration: none;
`

const ActionsContainer = styled.div`
  grid-area: description;
  align-self: end;
`

const StyledSummary = styled(Link)`
  margin-block: ${({ theme }) => theme.sizeByFactor(1.5)};
  text-decoration: none;
  display: block;
  grid-area: summary;
  border-radius: ${({ theme }) =>
    theme.cardBorderRadius ? `${theme.cardBorderRadius}px` : '0'};
  overflow: ${({ theme }) => (theme.cardBorderRadius ? 'hidden' : 'visible')};

  ${({ $layoutSizeType, theme }) =>
    $layoutSizeType === LayoutSizeTypes.LARGE &&
    `
    background-color: ${theme.colors.brandFoxtrot};
  `}
`

const StyledLabeledTexts = styled.div`
  display: flex;
`

const StyledDefinition = styled(Definition)`
  display: block;
  border-left: 1px solid ${({ theme }) => theme.colors.brandDelta};
  padding: 0 ${({ theme }) => theme.sizings.lvl2};
  margin: ${({ theme }) => theme.sizings.lvl2} 0;

  &:first-child {
    border-left: 0;
  }

  span {
    line-height: 1;
    padding-top: ${({ theme }) => theme.sizeByFactor(0.5)};
  }

  strong {
    display: block;
    line-height: 1;
    padding-top: ${({ theme }) => theme.sizeByFactor(1)};
    padding-bottom: ${({ theme }) => theme.sizeByFactor(0.5)};
  }

  ${media.tv`
    min-width: 140px;
  `}
`

const DescriptionDetails = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  padding-top: ${({ theme }) => theme.sizings.lvl0};
  gap: 0 ${({ theme }) => theme.sizings.lvl0};

  p,
  span,
  div {
    padding: 0;
    font-size: ${({ theme }) => theme.smallFontSize};
    position: relative;
  }

  ${media.phone`
    justify-content: flex-start;
    gap: 0 ${({ theme }) => theme.sizings.lvl2};

    p,
    span,
    div {
      font-size: ${({ theme }) => theme.baseFontSize};

    &:after {
      color: ${({ theme }) => theme.colors.brandDelta};
      content: '|';
      position: relative;
      left: ${({ theme }) => theme.sizings.lvl1};
    }

    &:last-child:after {
      content: '';
    }
    }
  `}
`

/**
 * Divider for list view on mobile used to separate "km", "year" and "price".
 * We must use the divider like this so we can use justify-content: space-between;
 * and the divider will be placed in the middle between the items. If we use :after,
 * like in the initial implementation, we then need to specify left: x to position it in the middle.
 */
const ListViewPhoneDivider = styled.div`
  color: ${({ theme }) => theme.colors.brandDelta};
`

const StyledExpected = styled(Text)`
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.brandDelta};
  display: flex;
  padding: ${({ theme }) => theme.sizings.lvl2};
`

const CarFileLabels = styled.ul`
  grid-area: actions;
  display: flex;
  flex-direction: row-reverse;
  gap: ${({ theme }) => theme.sizeByFactor(1)};
  align-self: center;

  // Use small layout for desktop, because of the width restriction caused by the filters
  ${media.desktop`
    align-self: center;
  `}

  // Ath TV size there is enough width for the large layout again
  ${media.tv`
    align-self: flex-end;
    padding-bottom: ${({ theme }) => theme.sizeByFactor(1.5)};
  `}

  > li {
    display: flex;
    align-items: center;
    justify-content: center;
    height: ${({ theme }) => theme.sizeByFactor(3)};
    width: ${({ theme }) => theme.sizeByFactor(3)};
    border-radius: 50%;
    background-color: ${({ $isDesktop, theme }) =>
      $isDesktop ? theme.colors.defaultBackground : 'transparent'};
  }

  ${({ $standard, theme }) =>
    $standard &&
    `
      && {
        align-self: flex-end;
        padding-bottom: 0;
      }

      > li {
        height: ${theme.sizeByFactor(2.625)};
        width: ${theme.sizeByFactor(2.625)};
      }
    `}
`

const StyledCarIdentityPlate = styled(CarIdentityPlate)`
  display: block;
  border-radius: 0;

  ${({ $isSelected }) =>
    $isSelected &&
    `
      border-left: none;
      border-bottom: none;
    `}

  ${({ $layoutSizeType, theme }) =>
    $layoutSizeType === LayoutSizeTypes.LARGE &&
    `
    margin-top: ${theme.sizeByFactor(1.5)};
    margin-left: auto;
  `}

  ${({ $layoutSizeType, theme }) =>
    $layoutSizeType === LayoutSizeTypes.SMALL &&
    `
      max-width: ${theme.sizeByFactor(10)};
  `}

  ${({ $overlay }) =>
    $overlay &&
    `
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      max-width: none;
  `}

  /* On mobile list view (SMALL) for RDC: hide the NL strip and drop its left padding so the plate fits */
  ${({ $layoutSizeType, theme }) =>
    theme.licensePlateFont &&
    $layoutSizeType === LayoutSizeTypes.SMALL &&
    `
      padding-left: 0 !important;
      > span {
        display: none !important;
      }
    `}

  ${media.tv`
    display: none;
  `}
`

const StyledCarIdentityPlateTV = styled(CarIdentityPlate)`
  grid-area: licensePlate;
  align-self: start;
  margin-top: 0;
  display: none;

  ${({ $layoutSizeType, theme }) =>
    $layoutSizeType === LayoutSizeTypes.LARGE &&
    `
    margin-top: ${theme.sizeByFactor(1.5)};
    margin-left: auto;
    /* Reserve a consistent height so cards with the (taller) license plate and
       cards with the (shorter) DOSSIER id-tag end up the same height, and
       vertically center the value within that reserved space. The && doubles
       specificity so 'display: flex' beats the plate atom's base
       'display: inline-block'. */
    min-height: ${theme.sizeByFactor(4)};
    && {
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }
  `}

  ${({ $layoutSizeType, theme }) =>
    $layoutSizeType === LayoutSizeTypes.SMALL &&
    `
      max-width: ${theme.sizeByFactor(10)};
  `}

  ${media.tv`
    display: block;
  `}
`

const StyledCarFileCardBids = styled(CarFileCardBids)`
  line-height: 1;
  padding: 0;

  ${media.tablet`
    padding-left: 0;
  `}

  ${media.desktop`
    padding-left: 0;
  `}

  ${media.tv`
    min-width: 205px;
    padding: ${({ theme }) => theme.sizings.lvl2};

    & div > span:last-of-type {
      position: relative;
      margin-top: ${({ theme }) => theme.sizeByFactor(-0.325)};
    }
  `}
`

const StyledCarCardOverviewWrapper = styled.div`
  grid-area: advertisement;
  display: flex;
  align-items: center;
  min-width: ${({ theme, $iconOnly }) =>
    $iconOnly ? 'auto' : theme.sizeByFactor(28)};
  cursor: pointer;
  padding: ${({ theme }) => theme.sizings.lvl2};
  padding-left: 0;
`

const CommentsIconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const CommentsBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: ${({ theme }) => theme.colors.actionsStandard};
  color: white;
  border-radius: 50%;
  min-width: ${({ theme }) => theme.sizings.lvl2};
  height: ${({ theme }) => theme.sizings.lvl2};
  font-size: ${({ theme }) => theme.sizeByFactor(1.25)};
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  line-height: 1;
`

const StyledMarktplaatsCarCard = styled(MarktplaatsCarCard)`
  background: ${({ $iconOnly }) => $iconOnly && 'transparent'};
  width: 100%;
  height: 100%;
  margin-left: 0;
`

export const LayoutSizeTypes = {
  LARGE: 'large',
  SMALL: 'small',
}

/**
 * List view version of the CarFileCard component.
 * This component displays a car file card in a list view format. It displays the content in 2
 * modes: small and large. The small mode is used for mobile and desktop, while the large mode is
 * used for tablet and TV. This has to do with the available width in the layout.
 */
function CarFileCardForList({
  carId,
  actions,
  appraisal,
  brand,
  createdExternally,
  expected,
  image,
  license,
  vin,
  km,
  link,
  location,
  margin,
  model,
  month,
  reportingCode,
  type,
  hideSummary,
  sellingPrice,
  exportPrice,
  exportPriceInclVat,
  status,
  staytime,
  year,
  publishedOnline,
  cleaningOrderIsIssued,
  hasRemarks,
  remarksCount,
  isCarShare,
  carShareBids,
  layoutSizeType,
  isSelected,
  onSelectChange,
  showCheckbox,
  hasSelectedCard,
  marktplaatsData,
  advertisementData,
  itemData,
  ...restProps
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [activeOverlay, setActiveOverlay] = useState(null) // e.g. 'marktplaats' | 'ads' | null
  const marktplaatsStatus = marktplaatsData?.status
  const [currentMarktplaatsStatus, setCurrentMarktplaatsStatus] =
    useState(marktplaatsStatus)
  useEffect(() => {
    setCurrentMarktplaatsStatus(marktplaatsStatus)
  }, [marktplaatsStatus])

  /**
   * Prevent native context menu on long-press,
   * This allows us to use long press to select a card instead of opening the default mobile pop-up for links (e.g. 'open in new tab').
   */
  // This allows us to use long press to select a card instead of opening the default mobile pop-up for links (e.g. 'open in new tab').
  const handleContextMenu = (event) => {
    event.preventDefault()
  }

  const theme = useContext(ThemeContext)
  const isPhone = useMediaLayout({ maxWidth: theme.metrics.phone - 1 })
  const isDesktop = useMediaLayout({ minWidth: theme.metrics.desktop })

  const openMarktplaatsOverlay = () => setActiveOverlay('marktplaats')
  const openAdsOverlay = () => setActiveOverlay('ads')
  const closeOverlay = () => setActiveOverlay(null)

  const handleMarktplaatsStatusChange = (newStatus) => {
    setCurrentMarktplaatsStatus(newStatus)
  }

  const routeLocation = useLocation()
  const allParams = getObjectFromQueryString(routeLocation.search)
  const selectedViewType = allParams.view
  const salesView = selectedViewType === CarFileViewTypeEnum.SALES
  const marktplaatsView = selectedViewType === CarFileViewTypeEnum.MARKTPLAATS
  const advertisementView = selectedViewType === CarFileViewTypeEnum.ADS
  const standardView = !salesView && !marktplaatsView && !advertisementView
  const carLabel = useMemo(() => {
    // When it's a carShare car, we want to display a static translation string
    if (isCarShare) {
      return t('carShare.carFileStockOverviewLabel')
    }

    // When the passed 'status' is a string in the CarFileStatusEnum (V3 / coming from the backend), then display the translated string for this status
    if (Object.values(CarFileStatusEnumLowerCase).includes(status)) {
      return t(`carFileStatussesV3.${status}`)
    }

    // When the above logic doesn't match, display the string that either has been given to this component or null, which shows no label in the Gallery Image
    return status
  }, [t, isCarShare, status])

  const appraisalText = isPrice(appraisal)
    ? formatRoundedPrice(appraisal)
    : t('pricesMissing')
  const sellingPriceText = isPrice(sellingPrice)
    ? formatRoundedPrice(sellingPrice)
    : t('pricesMissing')
  const showCarFileCardBids = isCarShare
    ? !!carShareBids?.deadline
    : !!carShareBids?.requested
  const isInProgress =
    status === CarFileStatusEnumLowerCase[CarFileStatusEnumUpperCase.lopend]
  const primaryPrice = isInProgress ? appraisal : sellingPrice
  const primaryPriceText = isPrice(primaryPrice)
    ? formatRoundedPrice(primaryPrice)
    : t('pricesMissing')
  const showStockDays =
    staytime !== null &&
    typeof staytime !== 'undefined' &&
    [
      CarFileStatusEnumLowerCase[CarFileStatusEnumUpperCase.binnen],
      CarFileStatusEnumLowerCase[CarFileStatusEnumUpperCase.verkocht],
      CarFileStatusEnumLowerCase[CarFileStatusEnumUpperCase.afgeleverd],
    ].includes(status)

  const handleSelectCar = () => {
    onSelectChange && onSelectChange(carId, !isSelected)
  }

  const handleMobileLongPress = useLongPress(
    handleSelectCar,
    {
      threshold: 500,
    },
    [],
  )

  const carFileLabels = (
    <CarFileLabels $isDesktop={isDesktop} $standard={standardView && !isPhone}>
      {!isCarShare && (
        <>
          <CarFileCardContextMenu
            carId={carId}
            reportingCode={reportingCode}
            mileage={km}
            license={license}
            isCarShare={isCarShare}
            showMarktplaatsItems={marktplaatsView && isPhone}
            onOpenMarktplaatsOverlay={openMarktplaatsOverlay}
            showAdvertisementItem={advertisementView && isPhone}
            onOpenAdvertisementOverlay={openAdsOverlay}
            itemData={itemData}
          />
          {!isPhone && (
            <>
              <li
                onClick={() =>
                  dispatch(
                    openOverlay('comments', {
                      data: { auto_id: carId },
                    }),
                  )
                }
              >
                <NewTooltip title={t('addComment')}>
                  <CommentsIconWrapper>
                    <Icon
                      color="actionsStandard"
                      size="lg"
                      type="carFileHasRemarks"
                    />
                    {remarksCount > 0 && (
                      <CommentsBadge>{remarksCount}</CommentsBadge>
                    )}
                  </CommentsIconWrapper>
                </NewTooltip>
              </li>
              {cleaningOrderIsIssued && (
                <li>
                  <NewTooltip title={t('carFileCleaningOrderIsIssued')}>
                    <Icon
                      type="cleaning"
                      color="actionsStandard"
                      width="22"
                      height="22"
                    />
                  </NewTooltip>
                </li>
              )}
              {publishedOnline && (
                <li>
                  <NewTooltip title={t('carFilePublishedOnline')}>
                    <Icon
                      color="actionsStandard"
                      size="lg"
                      type="carFilePublishedOnline"
                    />
                  </NewTooltip>
                </li>
              )}
              {/* Only show the createdExternally icon shen createdExternally === 1
                      it can also be 2 but this means that it was altered within UCC after
                      importing it from the external source
                      */}
              {createdExternally === 1 && (
                <li>
                  <NewTooltip title={t('carFileCreatedExternally')}>
                    <Icon
                      color="actionsStandard"
                      size="lg"
                      type="carFileCreatedExternally"
                    />
                  </NewTooltip>
                </li>
              )}
            </>
          )}
        </>
      )}
    </CarFileLabels>
  )

  return (
    <StyledCarCardWrapper {...restProps}>
      {showCheckbox && !isDesktop && (
        <Checkbox
          name={carId}
          checked={isSelected}
          onChange={(checked) =>
            onSelectChange && onSelectChange(carId, checked)
          }
          aria-label="select-car"
        />
      )}
      <Container
        {...(isPhone ? handleMobileLongPress() : null)}
        $layoutSizeType={layoutSizeType}
        $standard={standardView}
        $isSelected={isPhone && isSelected}
        onContextMenu={handleContextMenu}
      >
        <StyledGalleryImageLink
          to={link}
          $layoutSizeType={layoutSizeType}
          $standard={standardView}
          onContextMenu={handleContextMenu}
          // Preventing accidental navigation to carfile page due to misclick when selecting cards by making the image act as a label to the checkbox
          onClick={
            hasSelectedCard
              ? (e) => {
                  e.preventDefault()
                  handleSelectCar()
                }
              : undefined
          }
        >
          <SelectableGalleryImage
            src={image}
            alt={`A photo of ${carLabel}`}
            ratio={standardView ? 3 / 2 : 16 / 9}
            blurredBackground
            isSelected={isSelected}
            onSelectChange={onSelectChange}
            showCheckbox={showCheckbox}
            carId={carId}
          />
          {(!standardView || isPhone) && (
            <StyledCarIdentityPlate
              carFileId={carId}
              licensePlate={license}
              vin={vin}
              size={
                standardView
                  ? CarIdentityPlateSizes.MINI
                  : layoutSizeType === LayoutSizeTypes.LARGE
                    ? 'small'
                    : 'tiny'
              }
              $layoutSizeType={layoutSizeType}
              $isSelected={isSelected}
              $overlay={standardView}
            />
          )}
        </StyledGalleryImageLink>

        <DescriptionContainer
          to={link}
          $standard={standardView}
          onContextMenu={handleContextMenu}
          onClick={
            hasSelectedCard && isPhone
              ? (e) => {
                  e.preventDefault()
                  handleSelectCar()
                }
              : undefined
          }
        >
          {standardView ? (
            <>
              <CarFileCardTitle
                brand={brand}
                model={model}
                type={type}
                inline
              />
              {isPhone ? (
                <PhonePrices>
                  <span>{primaryPriceText}</span>
                  {isPrice(exportPrice) && (
                    <>
                      <PhonePriceDivider />
                      <span>
                        {`${t('exportPriceShort')}: ${formatRoundedPrice(
                          exportPrice,
                        )}`}
                      </span>
                    </>
                  )}
                </PhonePrices>
              ) : (
                <>
                  <CarKeySpecs items={[formatMileage(km), year, location]} />
                  {showStockDays && <StockDaysBadge days={staytime} />}
                </>
              )}
            </>
          ) : (
            <CarBrandAndModel
              carBrand={brand}
              carModel={model}
              carType={type}
              size={isPhone ? 'extraSmall' : 'small'}
              layoutType={
                layoutSizeType === LayoutSizeTypes.LARGE
                  ? CarBrandAndModelLayoutTypes.MODEL_TYPE_INLINED
                  : CarBrandAndModelLayoutTypes.INLINED
              }
              data-test-e2e="car-brand-and-model"
              listView
            />
          )}
          {!standardView && (
            <DescriptionDetails $layoutSizeType={layoutSizeType}>
              {isPhone && marktplaatsView && (
                <>
                  <StyledCarCardOverviewWrapper $iconOnly={isPhone}>
                    <StyledMarktplaatsCarCard
                      $iconOnly={isPhone}
                      status={currentMarktplaatsStatus}
                      carId={carId}
                      iconOnly={isPhone}
                      onClick={openMarktplaatsOverlay}
                      marktplaatsData={marktplaatsData}
                      onMarktplaatsStatusChange={handleMarktplaatsStatusChange}
                      listView
                    />
                  </StyledCarCardOverviewWrapper>
                </>
              )}
              {isPhone && advertisementView && advertisementData && (
                <StyledCarCardOverviewWrapper $iconOnly={isPhone}>
                  <AdvertisementCarCard
                    advertisementData={advertisementData}
                    $iconOnly={isPhone}
                    iconOnly={isPhone}
                    onClick={openAdsOverlay}
                    listView
                  />
                </StyledCarCardOverviewWrapper>
              )}
              {layoutSizeType === LayoutSizeTypes.SMALL &&
                !advertisementView &&
                !marktplaatsView && (
                  <>
                    {status !==
                      CarFileStatusEnumLowerCase[
                        CarFileStatusEnumUpperCase.lopend
                      ] && <Text type="body" text={sellingPriceText} />}
                    {status ===
                      CarFileStatusEnumLowerCase[
                        CarFileStatusEnumUpperCase.lopend
                      ] && (
                      <NewTooltip title={t('appraisal')}>
                        <Text type="body" text={appraisalText} />
                      </NewTooltip>
                    )}
                    {!salesView && !!margin && (
                      <NewTooltip title={t('margin')}>{margin}</NewTooltip>
                    )}
                    {expected && !salesView && (
                      <StyledExpected
                        text={`${t('inStockExpected')}: ${moment(
                          expected,
                        ).format(DateFormats.YEAR_MONTH_DAY)}`}
                      />
                    )}
                    {showCarFileCardBids && (
                      <StyledCarFileCardBids
                        size={CarFileCardBidsSize.SMALL}
                        layoutType={CarFileCardBidsLayoutType.HORIZONTAL}
                        carShareBids={carShareBids}
                      />
                    )}
                  </>
                )}
              {(marktplaatsView || advertisementView) && (
                <Text type="body" weight={500} text={sellingPriceText} />
              )}
              {isPhone && <ListViewPhoneDivider>|</ListViewPhoneDivider>}
              {typeof km !== 'undefined' && (
                <Text type="body" text={formatMileage(km)} />
              )}
              {isPhone && <ListViewPhoneDivider>|</ListViewPhoneDivider>}
              {(month || year) && (
                <Text
                  type="body"
                  text={`${t(getMonthNameByIndex(month))} ${year}`}
                />
              )}
              {typeof staytime !== 'undefined' &&
                !isPhone &&
                !salesView &&
                !marktplaatsView &&
                [
                  CarFileStatusEnumLowerCase[CarFileStatusEnumUpperCase.binnen],
                  CarFileStatusEnumLowerCase[
                    CarFileStatusEnumUpperCase.verkocht
                  ],
                  CarFileStatusEnumLowerCase[
                    CarFileStatusEnumUpperCase.afgeleverd
                  ],
                ].includes(status) && (
                  <Text type="body" text={`${staytime} ${t('stayDays')}`} />
                )}
              {location && !isPhone && <Text type="body" text={location} />}
            </DescriptionDetails>
          )}
        </DescriptionContainer>

        {!standardView && (
          <StyledCarIdentityPlateTV
            carFileId={carId}
            licensePlate={license}
            vin={vin}
            size={layoutSizeType === LayoutSizeTypes.LARGE ? 'small' : 'tiny'}
            $layoutSizeType={layoutSizeType}
          />
        )}

        {standardView && !isPhone ? (
          <IdentityColumn>
            <StandardPlate
              carFileId={carId}
              licensePlate={license}
              vin={vin}
              size={CarIdentityPlateSizes.COMPACT}
            />
            {carFileLabels}
          </IdentityColumn>
        ) : (
          carFileLabels
        )}
        {standardView && !isPhone && (
          <StandardSummaryLink to={link} onContextMenu={handleContextMenu}>
            <CarFileCardListPricing
              primaryLabel={isInProgress ? t('appraisal') : t('sellingPrice')}
              primaryValue={primaryPriceText}
              exportPrice={exportPrice}
              exportPriceInclVat={exportPriceInclVat}
            />
            {expected && (
              <Text
                type="body"
                text={`${t('inStockExpected')}: ${moment(expected).format(
                  DateFormats.YEAR_MONTH_DAY,
                )}`}
              />
            )}
            {showCarFileCardBids && (
              <StyledCarFileCardBids
                size={CarFileCardBidsSize.SMALL}
                layoutType={CarFileCardBidsLayoutType.HORIZONTAL}
                carShareBids={carShareBids}
              />
            )}
          </StandardSummaryLink>
        )}
        {actions && <ActionsContainer>{actions}</ActionsContainer>}
        {layoutSizeType === LayoutSizeTypes.LARGE &&
          !standardView &&
          !advertisementView &&
          !marktplaatsView && (
            <StyledSummary
              to={link}
              $layoutSizeType={layoutSizeType}
              onContextMenu={handleContextMenu}
              onClick={
                hasSelectedCard && isPhone
                  ? (e) => {
                      e.preventDefault()
                      handleSelectCar()
                    }
                  : undefined
              }
            >
              <StyledLabeledTexts>
                {showCarFileCardBids && (
                  <StyledCarFileCardBids
                    size={CarFileCardBidsSize.MEDIUM}
                    layoutType={CarFileCardBidsLayoutType.VERTICAL}
                    carShareBids={carShareBids}
                  />
                )}
                {status !==
                  CarFileStatusEnumLowerCase[
                    CarFileStatusEnumUpperCase.lopend
                  ] && (
                  <StyledDefinition
                    title={t('sellingPrice')}
                    value={sellingPriceText}
                  />
                )}
                {status ===
                  CarFileStatusEnumLowerCase[
                    CarFileStatusEnumUpperCase.lopend
                  ] && (
                  <StyledDefinition
                    title={t('appraisal')}
                    value={appraisalText}
                  />
                )}
                {!salesView && !!margin && (
                  <StyledDefinition title={t('margin')} value={margin} />
                )}
              </StyledLabeledTexts>
              {expected && !salesView && (
                <StyledExpected
                  text={`${t('inStockExpected')}: ${moment(expected).format(
                    DateFormats.YEAR_MONTH_DAY,
                  )}`}
                />
              )}
            </StyledSummary>
          )}
        {!isPhone && marktplaatsView && (
          <>
            <StyledCarCardOverviewWrapper $iconOnly={isPhone}>
              <StyledMarktplaatsCarCard
                $iconOnly={isPhone}
                status={currentMarktplaatsStatus}
                carId={carId}
                iconOnly={isPhone}
                onClick={openMarktplaatsOverlay}
                marktplaatsData={marktplaatsData}
                onMarktplaatsStatusChange={handleMarktplaatsStatusChange}
                listView
              />
            </StyledCarCardOverviewWrapper>
          </>
        )}
        {!isPhone && advertisementView && advertisementData && (
          <StyledCarCardOverviewWrapper $iconOnly={isPhone}>
            <AdvertisementCarCard
              advertisementData={advertisementData}
              $iconOnly={isPhone}
              iconOnly={isPhone}
              onClick={openAdsOverlay}
              listView
            />
          </StyledCarCardOverviewWrapper>
        )}
      </Container>

      {activeOverlay && (
        <MarktplaatsViewOverlay
          onClose={closeOverlay}
          image={image}
          brand={brand}
          model={model}
          type={type}
          licensePlate={license}
          mileage={km}
          year={year}
          sellingPrice={sellingPrice}
          carId={carId}
          marktplaatsInsights={marktplaatsData || {}}
          advertisementData={advertisementData || {}}
          selectedViewType={
            activeOverlay === 'marktplaats'
              ? CarFileViewTypeEnum.MARKTPLAATS
              : CarFileViewTypeEnum.ADVERTISEMENT
          }
        />
      )}
    </StyledCarCardWrapper>
  )
}

CarFileCardForList.propTypes = {
  actions: PropTypes.object,
  appraisal: PropTypes.number,
  brand: PropTypes.string,
  createdExternally: PropTypes.number,
  expected: PropTypes.string,
  carId: PropTypes.string.isRequired,
  image: PropTypes.string,
  license: PropTypes.string,
  vin: PropTypes.string,
  link: PropTypes.string.isRequired,
  km: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  month: PropTypes.string,
  margin: PropTypes.number,
  model: PropTypes.string,
  owner: PropTypes.string,
  part1: PropTypes.string,
  part2: PropTypes.string,
  reportingCode: PropTypes.string, // meldcode
  type: PropTypes.string,
  location: PropTypes.string,
  /**
   * optionally hide the summary block containing pricing info and standing/stay-time
   */
  hideSummary: PropTypes.bool,
  sellingPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  exportPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** True when the export price includes VAT (exportprijs), false for exportprijs_exbtw */
  exportPriceInclVat: PropTypes.bool,
  status: PropTypes.string,
  staytime: PropTypes.number,
  year: PropTypes.number,
  publishedOnline: PropTypes.bool,
  cleaningOrderIsIssued: PropTypes.bool,
  hasRemarks: PropTypes.bool,
  isCarShare: PropTypes.bool,
  layoutSizeType: PropTypes.oneOf(Object.values(LayoutSizeTypes)).isRequired,
  isSelected: PropTypes.bool,
  onSelectChange: PropTypes.func,
  showCheckbox: PropTypes.bool,
  hasSelectedCard: PropTypes.bool,
  marktplaatsData: PropTypes.object,
  advertisementData: PropTypes.object,
  itemData: PropTypes.object,
  ...carFileCardBidsPropTypes,
}

CarFileCardForList.defaultProps = {
  actions: null,
  appraisal: null,
  brand: '-',
  createdExternally: 0,
  expected: null,
  image: null,
  license: undefined,
  vin: undefined,
  location: undefined,
  km: 0,
  margin: null,
  month: null,
  model: '-',
  owner: null,
  part1: null,
  part2: null,
  reportingCode: null, // meldcode
  type: null,
  hideSummary: false,
  sellingPrice: null,
  exportPrice: null,
  exportPriceInclVat: false,
  status: null,
  staytime: null,
  year: null,
  publishedOnline: false,
  cleaningOrderIsIssued: false,
  hasRemarks: false,
  isCarShare: false,
  isSelected: false,
  onSelectChange: null,
  showCheckbox: false,
  hasSelectedCard: false,
  marktplaatsData: null,
  advertisementData: null,
  itemData: null,
  ...carFileCardBidsDefaultProps,
}

export default CarFileCardForList
